import React, { useState, useEffect } from 'react';
import { getUsers, inviteUser, deactivateUser, activateUser, updateRole } from '../api/users';
import { HiOutlinePlus, HiOutlineXMark } from 'react-icons/hi2';
import toast from 'react-hot-toast';

export default function Users() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ full_name: '', email: '', role: 'operator', mobile: '' });

  useEffect(() => { load(); }, []);

  const load = async () => {
    try { const res = await getUsers({ limit: 50 }); setUsers(res.data.data.users); }
    catch { } finally { setLoading(false); }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    try { await inviteUser(form); toast.success('User invited'); setShowModal(false); setForm({ full_name: '', email: '', role: 'operator', mobile: '' }); load(); }
    catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
  };

  const handleToggleStatus = async (user) => {
    try {
      if (user.status === 'active' || user.status === 'invited') { await deactivateUser(user.id); toast.success('User deactivated'); }
      else { await activateUser(user.id); toast.success('User activated'); }
      load();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
  };

  const handleRoleChange = async (userId, newRole) => {
    try { await updateRole(userId, { role: newRole }); toast.success('Role updated'); load(); }
    catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
  };

  const roleBadge = (r) => {
    const colors = { owner: 'bg-purple-100 text-purple-700', admin: 'bg-blue-100 text-blue-700', operator: 'bg-green-100 text-green-700', viewer: 'bg-gray-100 text-gray-600' };
    return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${colors[r] || ''}`}>{r}</span>;
  };

  const statusBadge = (s) => {
    const colors = { active: 'bg-green-100 text-green-700', inactive: 'bg-red-100 text-red-700', invited: 'bg-yellow-100 text-yellow-700' };
    return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${colors[s] || ''}`}>{s}</span>;
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <h1 className="text-xl font-bold text-dark-800">Team Members</h1>
        <button onClick={() => setShowModal(true)} className="flex items-center gap-2 px-4 py-2 bg-primary-500 text-white rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors">
          <HiOutlinePlus className="w-4 h-4" /> Invite User
        </button>
      </div>

      <div className="bg-white rounded-xl border border-dark-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-dark-200">
              <tr>
                <th className="text-left px-4 py-3 font-medium text-dark-500">Name</th>
                <th className="text-left px-4 py-3 font-medium text-dark-500 hidden sm:table-cell">Email</th>
                <th className="text-left px-4 py-3 font-medium text-dark-500">Role</th>
                <th className="text-left px-4 py-3 font-medium text-dark-500">Status</th>
                <th className="text-right px-4 py-3 font-medium text-dark-500">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-dark-100">
              {loading ? (
                <tr><td colSpan={5} className="text-center py-8 text-dark-400">Loading...</td></tr>
              ) : users.map((u) => (
                <tr key={u.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium">{u.full_name}</td>
                  <td className="px-4 py-3 text-dark-500 hidden sm:table-cell">{u.email}</td>
                  <td className="px-4 py-3">
                    {u.role === 'owner' ? roleBadge(u.role) : (
                      <select value={u.role} onChange={(e) => handleRoleChange(u.id, e.target.value)}
                        className="text-xs border border-dark-200 rounded px-2 py-1 outline-none">
                        <option value="admin">admin</option>
                        <option value="operator">operator</option>
                        <option value="viewer">viewer</option>
                      </select>
                    )}
                  </td>
                  <td className="px-4 py-3">{statusBadge(u.status)}</td>
                  <td className="px-4 py-3 text-right">
                    {u.role !== 'owner' && (
                      <button onClick={() => handleToggleStatus(u)}
                        className={`text-xs px-2 py-1 rounded ${u.status === 'active' || u.status === 'invited' ? 'text-red-500 hover:bg-red-50' : 'text-green-500 hover:bg-green-50'}`}>
                        {u.status === 'active' || u.status === 'invited' ? 'Deactivate' : 'Activate'}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl w-full max-w-md shadow-xl">
            <div className="flex justify-between items-center p-4 border-b border-dark-200">
              <h2 className="font-bold">Invite Team Member</h2>
              <button onClick={() => setShowModal(false)}><HiOutlineXMark className="w-5 h-5 text-dark-400" /></button>
            </div>
            <form onSubmit={handleInvite} className="p-4 space-y-3">
              <input type="text" required placeholder="Full name" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                className="w-full px-3 py-2 border border-dark-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-primary-500" />
              <input type="email" required placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="w-full px-3 py-2 border border-dark-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-primary-500" />
              <input type="text" placeholder="Mobile (optional)" value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })}
                className="w-full px-3 py-2 border border-dark-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-primary-500" />
              <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}
                className="w-full px-3 py-2 border border-dark-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-primary-500">
                <option value="admin">Admin</option>
                <option value="operator">Operator</option>
                <option value="viewer">Viewer</option>
              </select>
              <button type="submit" className="w-full bg-primary-500 text-white py-2 rounded-lg font-medium text-sm hover:bg-primary-600">Send Invite</button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
